import { Volume2, VolumeX } from 'lucide-react';
import { PrayerDisplay } from '../types/prayer';

interface MobileViewProps {
  prayers: PrayerDisplay[];
  nextPrayer: string;
  timeUntilNext: string;
  mutedPrayers: Set<string>;
  onToggleMute: (key: string) => void;
  currentTime: string;
  location: string;
  maghribBoomEnabled: boolean;
  onToggleBoom: () => void;
}

export function MobileView({
  prayers,
  nextPrayer,
  timeUntilNext,
  mutedPrayers,
  onToggleMute,
  currentTime,
  location,
  maghribBoomEnabled,
  onToggleBoom
}: MobileViewProps) {
  const nextPrayerObj = prayers.find(p => p.key === nextPrayer);
  const otherPrayers = prayers.filter(p => p.key !== nextPrayer && p.key !== 'sunrise');

  const getPrayerEmoji = (key: string) => {
    const emojiMap: Record<string, string> = {
      fajr: '🌅',
      sunrise: '☀️',
      dhuhr: '☀️',
      asr: '⛅',
      maghrib: '🌆',
      isha: '🌙'
    };
    return emojiMap[key] || '🕌';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900 px-3 py-4 pb-6">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="text-white/90 text-xs sm:text-sm mb-1 sm:mb-2">📍 {location}</div>
          <div className="text-white text-3xl sm:text-4xl font-bold mb-1">{currentTime}</div>
        </div>

        {/* Next Prayer - Large Card */}
        {nextPrayerObj && (
          <div className="mb-4 animate-fade-in">
            <div className="text-white/80 text-xs sm:text-sm text-center mb-2 uppercase tracking-wide">
              Next Prayer
            </div>
            <div className="bg-gradient-to-br from-teal-600 to-emerald-700 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}></div>
              </div>

              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-5xl sm:text-6xl">{getPrayerEmoji(nextPrayerObj.key)}</div>
                  {nextPrayerObj.key !== 'sunrise' && (
                    <button
                      onClick={() => onToggleMute(nextPrayerObj.key)}
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-2 sm:p-3 rounded-full transition-all"
                    >
                      {mutedPrayers.has(nextPrayerObj.key) ? (
                        <VolumeX className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      ) : (
                        <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      )}
                    </button>
                  )}
                </div>

                <div className="text-center">
                  <div className="text-white/90 text-lg sm:text-xl mb-1 sm:mb-2 font-arabic" dir="rtl">
                    {nextPrayerObj.nameArabic}
                  </div>
                  <div className="text-white text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                    {nextPrayerObj.name}
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-2 sm:mb-3">
                    <div className="text-white text-4xl sm:text-5xl font-bold">
                      {nextPrayerObj.time}
                    </div>
                  </div>
                  <div className="bg-emerald-900/40 backdrop-blur-sm rounded-lg sm:rounded-xl py-1.5 sm:py-2 px-3 sm:px-4 inline-block">
                    <div className="text-white/80 text-xs uppercase mb-0.5 sm:mb-1">Time Remaining</div>
                    <div className="text-white text-xl sm:text-2xl font-bold">{timeUntilNext}</div>
                  </div>
                </div>

                {/* Maghrib Boom Toggle */}
                {nextPrayerObj.key === 'maghrib' && (
                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/20">
                    <button
                      onClick={onToggleBoom}
                      className={`w-full py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold transition-all ${
                        maghribBoomEnabled
                          ? 'bg-yellow-500 text-gray-900'
                          : 'bg-white/20 text-white'
                      }`}
                    >
                      💥 Boom: {maghribBoomEnabled ? 'ON' : 'OFF'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Other Prayers - Compact List */}
        <div className="space-y-2">
          <div className="text-white/70 text-xs text-center mb-2 sm:mb-3 uppercase tracking-wide">
            All Prayers Today
          </div>
          {prayers.map((prayer) => (
            <div
              key={prayer.key}
              className={`rounded-lg sm:rounded-xl p-3 sm:p-4 transition-all ${
                prayer.key === nextPrayer
                  ? 'bg-white/10 backdrop-blur-sm border-2 border-white/30'
                  : 'bg-white/5 backdrop-blur-sm'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="text-2xl sm:text-3xl flex-shrink-0">{getPrayerEmoji(prayer.key)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                      <span className="text-white font-semibold text-base sm:text-lg">
                        {prayer.name}
                      </span>
                      {prayer.key === nextPrayer && (
                        <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                          NEXT
                        </span>
                      )}
                    </div>
                    <div className="text-white/70 text-xs sm:text-sm font-arabic truncate" dir="rtl">
                      {prayer.nameArabic}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2">
                  <div className="text-white text-xl sm:text-2xl font-bold">
                    {prayer.time}
                  </div>
                  {prayer.key !== 'sunrise' && (
                    <button
                      onClick={() => onToggleMute(prayer.key)}
                      className="bg-white/10 hover:bg-white/20 p-1.5 sm:p-2 rounded-lg transition-all"
                    >
                      {mutedPrayers.has(prayer.key) ? (
                        <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/70" />
                      ) : (
                        <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Maghrib Boom Toggle for list item */}
              {prayer.key === 'maghrib' && prayer.key !== nextPrayer && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <button
                    onClick={onToggleBoom}
                    className={`w-full py-1.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                      maghribBoomEnabled
                        ? 'bg-yellow-500 text-gray-900'
                        : 'bg-white/10 text-white/80'
                    }`}
                  >
                    💥 Boom: {maghribBoomEnabled ? 'ON' : 'OFF'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Message */}
        <div className="mt-8 text-center text-white/60 text-sm">
          <p>May Allah accept your prayers</p>
        </div>
      </div>
    </div>
  );
}
